<template>
  <layout-default>
    <div class="stepper-wrapper">
        <DescuentoCupon 
          v-if="currentStep <= 3"
          :student-data="couponData.studentData"
          :program-data="couponData.programData"
          :selected-courses="couponData.selectedCourses"
          :location-id="couponData.locationId"
          :current-coupon="step4.coupon"
          @coupon-applied="handleCouponApplied"
          @coupon-removed="handleCouponRemoved"
        />

      <!-- Stepper Component -->
      <Stepper 
        :initial-step="currentStep" 
        :total-steps="5"
        @step-changed="handleStepChange"
      />
      
      <!-- Step Content -->
      <div class="step-content">
        <!-- Paso 1: Selección de curso y programa -->
        <Step1 
          v-if="currentStep === 1"
          :stepper-data="step1"
          @update-data="updateStep1Data"
          @next-step="nextStep"
        />
        
        <!-- Paso 2: Datos personales -->
        <Step2 
          v-if="currentStep === 2"
          :stepper-data="step2"
          :step1-data="step1"
          @update-data="updateStep2Data"
          @next-step="nextStep"
          @previous-step="previousStep"
        />
        
        <!-- Paso 3: Selección de mes y cursos -->
        <Step3 
          v-if="currentStep === 3"
          :stepper-data="step3"
          :step1-data="step1"
          :step2-data="step2"
          @update-data="updateStep3Data"
          @update-step2-location="updateStep2Location"
          @next-step="nextStep"
          @previous-step="previousStep"
        />
        
        <!-- Paso 4: Confirmación de datos -->
        <Step4 
          v-if="currentStep === 4"
          :stepper-data="step4"
          :all-data="allData"
          @update-data="updateStep4Data"
          @quote-updated="handleQuoteUpdated"
          @next-step="nextStep"
          @previous-step="previousStep"
        />
        
        <!-- Paso 5: Información personal -->
        <Step5 
          v-if="currentStep === 5"
          :stepper-data="allData"
          @update-data="updateStep5Data"
          @update-step2="updateStep2Data"
          @previous-step="previousStep"
          @complete-process="completeProcess"
        />
      </div>
    </div>
  </layout-default>
</template>

<script>
import LayoutDefault from '@/layouts/Default.vue'
import Stepper from './Stepper.vue'
import Step1 from './Step1.vue'
import Step2 from './Step2.vue'
import Step3 from './Step3.vue'
import Step4 from './Step4.vue'
import Step5 from './Step5.vue'
import { useStepperStore } from '@/store/stepperStore'
import DescuentoCupon from '@/components/featured/descuento-cupon.vue'

export default {
  name: 'StepperWrapper',
  
  components: {
    LayoutDefault,
    Stepper,
    Step1,
    Step2,
    Step3,
    DescuentoCupon,
    Step4,
    Step5
  },

  data() {
    return {
      couponData: {
        studentData: {},
        programData: {},
        selectedCourses: [],
        locationId: null
      }
    }
  },
  setup() {
    const stepperStore = useStepperStore()
    
    // Cargar datos guardados al inicializar
    stepperStore.loadFromLocalStorage()
    
    return {
      // Estado reactivo del stepper
      currentStep: stepperStore.currentStep,
      step1: stepperStore.step1,
      step2: stepperStore.step2,
      step3: stepperStore.step3,
      step4: stepperStore.step4,
      step5: stepperStore.step5,
      
      // Métodos del stepper
      setCurrentStep: stepperStore.setCurrentStep,
      nextStep: stepperStore.nextStep,
      previousStep: stepperStore.previousStep,
      updateStep1: stepperStore.updateStep1,
      updateStep2: stepperStore.updateStep2,
      updateStep3: stepperStore.updateStep3,
      updateStep4: stepperStore.updateStep4,
      updateStep5: stepperStore.updateStep5,
      setCoupon: stepperStore.setCoupon,
      getAllData: stepperStore.getAllData,
      saveToLocalStorage: stepperStore.saveToLocalStorage
    }
  },
  
  computed: {
    allData() {
      return this.getAllData()
    }
  },
  
  methods: {
    // Manejar cambio de paso desde el stepper
    handleStepChange(step) {
      this.setCurrentStep(step)
      this.saveToLocalStorage()
    },
    
    // Actualizar datos del Paso 1
    updateStep1Data(data) {
      this.updateStep1(data)
      this.saveToLocalStorage()
    },
    
    // Actualizar datos del Paso 2
    updateStep2Data(data) {
      this.updateStep2(data)
      this.saveToLocalStorage()
    },
    
    // Actualizar datos del Paso 3
    updateStep3Data(data) {
      // Copia studentId y orderId de step2 si existen
      if (this.step2.studentId) data.studentId = this.step2.studentId
      if (this.step2.orderId) data.orderId = this.step2.orderId
      
      // Si hay una cotización, guardarla en step4
      if (data.quote) {
        this.updateStep4({ quote: data.quote })
        // Remover quote de data para no guardarlo en step3
        const step3Data = { ...data }
        delete step3Data.quote
        this.updateStep3(step3Data)
      } else {
        this.updateStep3(data)
      }
      
      // Preservar el cupón existente en step4 si existe
      if (this.step4.coupon) {
        this.updateStep4({ coupon: this.step4.coupon })
      }
      
      this.saveToLocalStorage()
    },
    
    // Actualizar preferredLocation del Paso 2 cuando cambia la sede en Paso 3
    updateStep2Location(sedeInfo) {
      this.updateStep2({ preferredLocation: sedeInfo })
      this.saveToLocalStorage()
    },
    

    
    // Actualizar datos del Paso 4
    updateStep4Data(data) {
      this.updateStep4(data)
      this.saveToLocalStorage()
    },
    
    // Manejar actualización de cotización desde cupón
    handleQuoteUpdated(newQuote) {
      // Preservar el cupón existente al actualizar la cotización
      const currentCoupon = this.step4.coupon;
      this.updateStep4({ 
        quote: newQuote,
        coupon: currentCoupon // Mantener el cupón existente
      })
      this.saveToLocalStorage()
    },
    
    // Actualizar datos del Paso 5
    updateStep5Data(data) {
      this.updateStep5(data)
      this.saveToLocalStorage()
    },
    
    // Navegar al siguiente paso
    goToNextStep() {
      this.nextStep()
      this.saveToLocalStorage()
    },
    
    // Navegar al paso anterior
    goToPreviousStep() {
      this.previousStep()
      this.saveToLocalStorage()
    },
    
    // Completar todo el proceso
    completeProcess() {
      this.$router.push({
        path: '/inscripcion/completado',
        query: this.$route.query
      })
    },

    handleCouponApplied(couponData) {
      if (couponData.isTemporary) {
        // Guardar cupón directamente en step4.coupon
        this.updateStep4({ coupon: couponData.coupon })
        this.saveToLocalStorage()
      } else {
        // Manejar cupón validado (para compatibilidad futura)
        this.appliedCoupon = {
          coupon: couponData.coupon,
          isValid: couponData.isValid,
          discount: couponData.discount
        }
      }
    },

    handleCouponRemoved() {
      this.appliedCoupon = null
      this.updateStep4({ coupon: null }) // Limpiar el cupón de step4
      this.saveToLocalStorage()
    },
    
    /**
     * Preserva el cupón cuando se navega entre pasos
     */
    preserveCouponOnNavigation() {
      // Si hay un cupón aplicado en step4, asegurarse de que se mantenga
      if (this.step4.coupon) {
        // Preservar el cupón en todos los pasos para que pueda ser validado automáticamente
        this.updateStep4({ coupon: this.step4.coupon })
        this.saveToLocalStorage()
      }
    },
    
    /**
     * Maneja el cupón según la navegación entre pasos
     */
    handleCouponOnNavigation(newStep, oldStep) {
      
      // Si se navega hacia atrás desde el paso 4, limpiar el cupón
      if (oldStep === 4 && newStep < 4) {
        this.updateStep4({ coupon: null })
        this.saveToLocalStorage()
        return
      }
      
      // Si se navega hacia adelante y hay cupón, preservarlo
      if (newStep > oldStep && this.step4.coupon) {
        this.preserveCouponOnNavigation()
        return
      }
      
    }
  },
  
  // Observar cambios en el paso actual para actualizar la URL
  watch: {
    currentStep(newStep, oldStep) {
      
      // Verificar si ya estamos en la ruta correcta para evitar navegación duplicada
      const expectedPath = `/inscripcion/paso-${newStep}`
      if (this.$route.path !== expectedPath) {
        // Actualizar la URL sin recargar la página
        this.$router.replace({
          path: expectedPath,
          query: { ...this.$route.query }
        }).catch(err => {
          // Ignorar errores de navegación duplicada
          if (err.name !== 'NavigationDuplicated') {
            console.error('Navigation error:', err)
          }
        })
      }
      
      // Manejar el cupón según la navegación
      this.handleCouponOnNavigation(newStep, oldStep)
    }
  },
  
  // Cargar datos al montar el componente
  mounted() {
    // Si hay un paso específico en la URL, navegar a él
    const path = this.$route.path
    const stepMatch = path.match(/paso-(\d+)/)
    if (stepMatch) {
      const step = parseInt(stepMatch[1])
      if (step >= 1 && step <= 5) {
        this.setCurrentStep(step)
      }
    }
  }
}
</script>

<style scoped>
.stepper-wrapper {
  min-height: 100vh;
  background-color: #fff;
}

.step-content {
  max-width: 936px;
  margin: 0 auto;
}

/* Responsive Design */
@media (max-width: 768px) {
  .step-content {
    padding: 15px;
  }
}

@media (max-width: 480px) {
  .step-content {
    padding: 10px;
  }
}
</style> 